import React, { useState, useEffect } from "react";
import {
  Stethoscope,
  Shield,
  User,
  Phone,
  MapPin,
  Calendar,
  Building2,
  Save,
  AlertCircle,
  CheckCircle,
  Clock,
  FileText,
  Key,
  Users,
} from "lucide-react";
import { httpsCallable } from "firebase/functions";
import { functions } from "../firebase";
import { useTheme } from "../contexts/ThemeContext";

const VeterinarianDashboardPage = ({ userRole }) => {
  const { themeClasses } = useTheme();

  const [formData, setFormData] = useState({
    petData: {
      name: "",
      species: "Dog",
      breed: "",
      birthDate: "",
      imageUrl: "",
      ownerId: "",
      microchipId: "",
      weight: "",
      color: "",
    },
    vaccinationData: {
      vaccineName: "",
      manufacturer: "",
      batchNumber: "",
      dateAdministered: "",
      validUntil: "",
      vetName: "",
      vetContact: "",
      vetLicense: "",
      clinicAddress: "",
      notes: "",
    },
    authentication: {
      vetApiKey: "",
      clinicId: "",
      timestamp: "",
    },
  });

  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [errors, setErrors] = useState({});
  const [apiKeys, setApiKeys] = useState([]);

  // Common vaccines for quick selection
  const commonVaccines = [
    "Rabies",
    "DHPP (Distemper, Hepatitis, Parvovirus, Parainfluenza)",
    "Bordetella",
    "Lyme Disease",
    "Canine Influenza",
    "FVRCP (Feline Viral Rhinotracheitis, Calicivirus, Panleukopenia)",
    "FeLV (Feline Leukemia)",
    "FIV (Feline Immunodeficiency)",
  ];

  const species = [
    "Dog",
    "Cat",
    "Bird",
    "Rabbit",
    "Ferret",
    "Reptile",
    "Other",
  ];

  useEffect(() => {
    // Set default timestamp
    setFormData((prev) => ({
      ...prev,
      authentication: {
        ...prev.authentication,
        timestamp: new Date().toISOString(),
      },
    }));

    // Load saved API keys from localStorage for convenience
    const savedApiKeys = localStorage.getItem("vetApiKeys");
    if (savedApiKeys) {
      setApiKeys(JSON.parse(savedApiKeys));
    }
  }, []);

  const handleInputChange = (section, field, value) => {
    setFormData((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value,
      },
    }));

    // Clear errors when user starts typing
    if (errors[`${section}.${field}`]) {
      setErrors((prev) => ({ ...prev, [`${section}.${field}`]: null }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Validate pet data
    if (!formData.petData.name.trim()) {
      newErrors["petData.name"] = "Pet name is required";
    }
    if (!formData.petData.breed.trim()) {
      newErrors["petData.breed"] = "Breed is required";
    }
    if (!formData.petData.birthDate) {
      newErrors["petData.birthDate"] = "Birth date is required";
    }
    if (!formData.petData.ownerId.trim()) {
      newErrors["petData.ownerId"] = "Owner ID is required";
    }

    // Validate vaccination data
    if (!formData.vaccinationData.vaccineName.trim()) {
      newErrors["vaccinationData.vaccineName"] = "Vaccine name is required";
    }
    if (!formData.vaccinationData.manufacturer.trim()) {
      newErrors["vaccinationData.manufacturer"] = "Manufacturer is required";
    }
    if (!formData.vaccinationData.batchNumber.trim()) {
      newErrors["vaccinationData.batchNumber"] = "Batch number is required";
    }
    if (!formData.vaccinationData.dateAdministered) {
      newErrors["vaccinationData.dateAdministered"] =
        "Administration date is required";
    }
    if (!formData.vaccinationData.validUntil) {
      newErrors["vaccinationData.validUntil"] = "Valid until date is required";
    }
    if (!formData.vaccinationData.vetName.trim()) {
      newErrors["vaccinationData.vetName"] = "Veterinarian name is required";
    }
    if (!formData.vaccinationData.vetContact.trim()) {
      newErrors["vaccinationData.vetContact"] =
        "Contact information is required";
    }
    if (!formData.vaccinationData.vetLicense.trim()) {
      newErrors["vaccinationData.vetLicense"] = "License number is required";
    }
    if (!formData.vaccinationData.clinicAddress.trim()) {
      newErrors["vaccinationData.clinicAddress"] = "Clinic address is required";
    }

    // Validate authentication
    if (!formData.authentication.vetApiKey.trim()) {
      newErrors["authentication.vetApiKey"] = "API key is required";
    }
    if (!formData.authentication.clinicId.trim()) {
      newErrors["authentication.clinicId"] = "Clinic ID is required";
    }

    // Validate date logic
    if (
      formData.vaccinationData.dateAdministered &&
      formData.vaccinationData.validUntil
    ) {
      const adminDate = new Date(formData.vaccinationData.dateAdministered);
      const validDate = new Date(formData.vaccinationData.validUntil);

      if (adminDate >= validDate) {
        newErrors["vaccinationData.validUntil"] =
          "Valid until date must be after administration date";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setResult(null);

    try {
      const storePetVaccinationData = httpsCallable(
        functions,
        "storePetVaccinationData"
      );
      const response = await storePetVaccinationData(formData);

      setResult({
        success: true,
        data: response.data,
      });

      // Save API key for future use (if user wants)
      if (
        formData.authentication.vetApiKey &&
        !apiKeys.includes(formData.authentication.vetApiKey)
      ) {
        const newApiKeys = [...apiKeys, formData.authentication.vetApiKey];
        setApiKeys(newApiKeys);
        localStorage.setItem("vetApiKeys", JSON.stringify(newApiKeys));
      }

      // Reset form
      setFormData((prev) => ({
        petData: {
          name: "",
          species: "Dog",
          breed: "",
          birthDate: "",
          imageUrl: "",
          ownerId: "",
          microchipId: "",
          weight: "",
          color: "",
        },
        vaccinationData: {
          vaccineName: "",
          manufacturer: "",
          batchNumber: "",
          dateAdministered: "",
          validUntil: "",
          vetName: "",
          vetContact: "",
          vetLicense: "",
          clinicAddress: "",
          notes: "",
        },
        authentication: {
          ...prev.authentication,
          timestamp: new Date().toISOString(),
        },
      }));
    } catch (error) {
      console.error("Error submitting vaccination data:", error);
      setResult({
        success: false,
        error: error.message || "Failed to submit vaccination data",
      });
    } finally {
      setLoading(false);
    }
  };

  // Only show to admin users
  if (userRole !== "admin") {
    return (
      <div className="text-center py-12">
        <Stethoscope
          className={`w-16 h-16 mx-auto mb-4 ${themeClasses.text.secondary}`}
        />
        <h3
          className={`text-xl font-semibold mb-2 ${themeClasses.text.primary}`}
        >
          Access Restricted
        </h3>
        <p className={themeClasses.text.secondary}>
          This section is only available to veterinarian administrators.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1
            className={`text-2xl font-bold flex items-center ${themeClasses.text.primary}`}
          >
            <Stethoscope className="w-6 h-6 mr-2 text-cyan-400" />
            Veterinarian Dashboard
          </h1>
          <p className={`mt-1 ${themeClasses.text.secondary}`}>
            Submit vaccination records for pets
          </p>
        </div>
      </div>

      {/* Result Display */}
      {result && (
        <div
          className={`rounded-lg border p-4 ${
            result.success
              ? "bg-green-500/10 border-green-500/50"
              : "bg-red-500/10 border-red-500/50"
          }`}
        >
          <div className="flex items-center">
            {result.success ? (
              <CheckCircle className="w-5 h-5 text-green-400 mr-2" />
            ) : (
              <AlertCircle className="w-5 h-5 text-red-400 mr-2" />
            )}
            <div>
              <h3
                className={`font-semibold ${
                  result.success ? "text-green-400" : "text-red-400"
                }`}
              >
                {result.success
                  ? "Vaccination Record Submitted Successfully"
                  : "Submission Failed"}
              </h3>
              {result.success ? (
                <div className={`text-sm mt-1 ${themeClasses.text.secondary}`}>
                  <p>Pet ID: {result.data.petId}</p>
                  <p>Vaccination ID: {result.data.vaccinationId}</p>
                  <p>
                    Passport Eligible:{" "}
                    {result.data.passportEligible ? "Yes" : "No"}
                  </p>
                </div>
              ) : (
                <p className={`text-sm mt-1 ${themeClasses.text.secondary}`}>
                  {result.error}
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Authentication Section */}
        <div
          className={`rounded-lg border p-6 ${themeClasses.bg.card} ${themeClasses.border.primary}`}
        >
          <h3
            className={`text-lg font-semibold mb-4 flex items-center ${themeClasses.text.primary}`}
          >
            <Key className="w-5 h-5 mr-2 text-yellow-400" />
            Authentication
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label
                className={`block text-sm mb-2 ${themeClasses.text.secondary}`}
              >
                Veterinarian API Key *
              </label>
              <div className="relative">
                <input
                  type="password"
                  value={formData.authentication.vetApiKey}
                  onChange={(e) =>
                    handleInputChange(
                      "authentication",
                      "vetApiKey",
                      e.target.value
                    )
                  }
                  className={`w-full border rounded-lg px-3 py-2 focus:outline-none focus:border-cyan-500 ${
                    themeClasses.bg.input
                  } ${themeClasses.text.primary} ${
                    errors["authentication.vetApiKey"]
                      ? "border-red-500"
                      : themeClasses.border.primary
                  }`}
                  placeholder="Enter your API key"
                />
                {apiKeys.length > 0 && (
                  <select
                    onChange={(e) => {
                      if (e.target.value) {
                        handleInputChange(
                          "authentication",
                          "vetApiKey",
                          e.target.value
                        );
                      }
                    }}
                    className={`absolute right-0 top-0 h-full bg-transparent border-none pr-3 ${themeClasses.text.secondary}`}
                    defaultValue=""
                  >
                    <option value="">Saved Keys</option>
                    {apiKeys.map((key, index) => (
                      <option key={index} value={key}>
                        {key.substring(0, 10)}...
                      </option>
                    ))}
                  </select>
                )}
              </div>
              {errors["authentication.vetApiKey"] && (
                <p className="text-red-400 text-sm mt-1">
                  {errors["authentication.vetApiKey"]}
                </p>
              )}
            </div>

            <div>
              <label
                className={`block text-sm mb-2 ${themeClasses.text.secondary}`}
              >
                Clinic ID *
              </label>
              <input
                type="text"
                value={formData.authentication.clinicId}
                onChange={(e) =>
                  handleInputChange(
                    "authentication",
                    "clinicId",
                    e.target.value
                  )
                }
                className={`w-full border rounded-lg px-3 py-2 focus:outline-none focus:border-cyan-500 ${
                  themeClasses.bg.input
                } ${themeClasses.text.primary} ${
                  errors["authentication.clinicId"]
                    ? "border-red-500"
                    : themeClasses.border.primary
                }`}
                placeholder="e.g., clinic_oslo_demo"
              />
              {errors["authentication.clinicId"] && (
                <p className="text-red-400 text-sm mt-1">
                  {errors["authentication.clinicId"]}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Pet Information Section */}
        <div
          className={`rounded-lg border p-6 ${themeClasses.bg.card} ${themeClasses.border.primary}`}
        >
          <h3
            className={`text-lg font-semibold mb-4 flex items-center ${themeClasses.text.primary}`}
          >
            <Users className="w-5 h-5 mr-2 text-blue-400" />
            Pet Information
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label
                className={`block text-sm mb-2 ${themeClasses.text.secondary}`}
              >
                Pet Name *
              </label>
              <input
                type="text"
                value={formData.petData.name}
                onChange={(e) =>
                  handleInputChange("petData", "name", e.target.value)
                }
                className={`w-full border rounded-lg px-3 py-2 focus:outline-none focus:border-cyan-500 ${
                  themeClasses.bg.input
                } ${themeClasses.text.primary} ${
                  errors["petData.name"]
                    ? "border-red-500"
                    : themeClasses.border.primary
                }`}
                placeholder="e.g., Whiskers"
              />
              {errors["petData.name"] && (
                <p className="text-red-400 text-sm mt-1">
                  {errors["petData.name"]}
                </p>
              )}
            </div>

            <div>
              <label
                className={`block text-sm mb-2 ${themeClasses.text.secondary}`}
              >
                Species *
              </label>
              <select
                value={formData.petData.species}
                onChange={(e) =>
                  handleInputChange("petData", "species", e.target.value)
                }
                className={`w-full border rounded-lg px-3 py-2 focus:outline-none focus:border-cyan-500 ${themeClasses.bg.input} ${themeClasses.text.primary} ${themeClasses.border.primary}`}
              >
                {species.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label
                className={`block text-sm mb-2 ${themeClasses.text.secondary}`}
              >
                Breed *
              </label>
              <input
                type="text"
                value={formData.petData.breed}
                onChange={(e) =>
                  handleInputChange("petData", "breed", e.target.value)
                }
                className={`w-full border rounded-lg px-3 py-2 focus:outline-none focus:border-cyan-500 ${
                  themeClasses.bg.input
                } ${themeClasses.text.primary} ${
                  errors["petData.breed"]
                    ? "border-red-500"
                    : themeClasses.border.primary
                }`}
                placeholder="e.g., Golden Retriever"
              />
              {errors["petData.breed"] && (
                <p className="text-red-400 text-sm mt-1">
                  {errors["petData.breed"]}
                </p>
              )}
            </div>

            <div>
              <label
                className={`block text-sm mb-2 ${themeClasses.text.secondary}`}
              >
                Birth Date *
              </label>
              <input
                type="date"
                value={formData.petData.birthDate}
                onChange={(e) =>
                  handleInputChange("petData", "birthDate", e.target.value)
                }
                className={`w-full border rounded-lg px-3 py-2 focus:outline-none focus:border-cyan-500 ${
                  themeClasses.bg.input
                } ${themeClasses.text.primary} ${
                  errors["petData.birthDate"]
                    ? "border-red-500"
                    : themeClasses.border.primary
                }`}
              />
              {errors["petData.birthDate"] && (
                <p className="text-red-400 text-sm mt-1">
                  {errors["petData.birthDate"]}
                </p>
              )}
            </div>

            <div>
              <label
                className={`block text-sm mb-2 ${themeClasses.text.secondary}`}
              >
                Owner ID *
              </label>
              <input
                type="text"
                value={formData.petData.ownerId}
                onChange={(e) =>
                  handleInputChange("petData", "ownerId", e.target.value)
                }
                className={`w-full border rounded-lg px-3 py-2 focus:outline-none focus:border-cyan-500 ${
                  themeClasses.bg.input
                } ${themeClasses.text.primary} ${
                  errors["petData.ownerId"]
                    ? "border-red-500"
                    : themeClasses.border.primary
                }`}
                placeholder="e.g., user_abc123"
              />
              {errors["petData.ownerId"] && (
                <p className="text-red-400 text-sm mt-1">
                  {errors["petData.ownerId"]}
                </p>
              )}
            </div>

            <div>
              <label
                className={`block text-sm mb-2 ${themeClasses.text.secondary}`}
              >
                Microchip ID
              </label>
              <input
                type="text"
                value={formData.petData.microchipId}
                onChange={(e) =>
                  handleInputChange("petData", "microchipId", e.target.value)
                }
                className={`w-full border rounded-lg px-3 py-2 focus:outline-none focus:border-cyan-500 ${themeClasses.bg.input} ${themeClasses.text.primary} ${themeClasses.border.primary}`}
                placeholder="e.g., 123456789012345"
              />
            </div>

            <div>
              <label
                className={`block text-sm mb-2 ${themeClasses.text.secondary}`}
              >
                Weight (kg)
              </label>
              <input
                type="number"
                step="0.1"
                value={formData.petData.weight}
                onChange={(e) =>
                  handleInputChange("petData", "weight", e.target.value)
                }
                className={`w-full border rounded-lg px-3 py-2 focus:outline-none focus:border-cyan-500 ${themeClasses.bg.input} ${themeClasses.text.primary} ${themeClasses.border.primary}`}
                placeholder="e.g., 4.5"
              />
            </div>

            <div>
              <label
                className={`block text-sm mb-2 ${themeClasses.text.secondary}`}
              >
                Color
              </label>
              <input
                type="text"
                value={formData.petData.color}
                onChange={(e) =>
                  handleInputChange("petData", "color", e.target.value)
                }
                className={`w-full border rounded-lg px-3 py-2 focus:outline-none focus:border-cyan-500 ${themeClasses.bg.input} ${themeClasses.text.primary} ${themeClasses.border.primary}`}
                placeholder="e.g., Brown"
              />
            </div>

            <div>
              <label
                className={`block text-sm mb-2 ${themeClasses.text.secondary}`}
              >
                Image URL
              </label>
              <input
                type="url"
                value={formData.petData.imageUrl}
                onChange={(e) =>
                  handleInputChange("petData", "imageUrl", e.target.value)
                }
                className={`w-full border rounded-lg px-3 py-2 focus:outline-none focus:border-cyan-500 ${themeClasses.bg.input} ${themeClasses.text.primary} ${themeClasses.border.primary}`}
                placeholder="https://example.com/pet-image.jpg"
              />
            </div>
          </div>
        </div>

        {/* Vaccination Information Section */}
        <div
          className={`rounded-lg border p-6 ${themeClasses.bg.card} ${themeClasses.border.primary}`}
        >
          <h3
            className={`text-lg font-semibold mb-4 flex items-center ${themeClasses.text.primary}`}
          >
            <Shield className="w-5 h-5 mr-2 text-green-400" />
            Vaccination Information
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label
                className={`block text-sm mb-2 ${themeClasses.text.secondary}`}
              >
                Vaccine Name *
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={formData.vaccinationData.vaccineName}
                  onChange={(e) =>
                    handleInputChange(
                      "vaccinationData",
                      "vaccineName",
                      e.target.value
                    )
                  }
                  className={`w-full border rounded-lg px-3 py-2 focus:outline-none focus:border-cyan-500 ${
                    themeClasses.bg.input
                  } ${themeClasses.text.primary} ${
                    errors["vaccinationData.vaccineName"]
                      ? "border-red-500"
                      : themeClasses.border.primary
                  }`}
                  placeholder="e.g., Rabies"
                  list="vaccines"
                />
                <datalist id="vaccines">
                  {commonVaccines.map((vaccine) => (
                    <option key={vaccine} value={vaccine} />
                  ))}
                </datalist>
              </div>
              {errors["vaccinationData.vaccineName"] && (
                <p className="text-red-400 text-sm mt-1">
                  {errors["vaccinationData.vaccineName"]}
                </p>
              )}
            </div>

            <div>
              <label
                className={`block text-sm mb-2 ${themeClasses.text.secondary}`}
              >
                Manufacturer *
              </label>
              <input
                type="text"
                value={formData.vaccinationData.manufacturer}
                onChange={(e) =>
                  handleInputChange(
                    "vaccinationData",
                    "manufacturer",
                    e.target.value
                  )
                }
                className={`w-full border rounded-lg px-3 py-2 ${
                  themeClasses.bg.input
                } ${
                  themeClasses.text.primary
                } focus:outline-none focus:border-cyan-500 ${
                  errors["vaccinationData.manufacturer"]
                    ? "border-red-500"
                    : "themeClasses.border.primary"
                }`}
                placeholder="e.g., Nobivac"
              />
              {errors["vaccinationData.manufacturer"] && (
                <p className="text-red-400 text-sm mt-1">
                  {errors["vaccinationData.manufacturer"]}
                </p>
              )}
            </div>

            <div>
              <label
                className={`block text-sm mb-2 ${themeClasses.text.secondary}`}
              >
                Batch Number *
              </label>
              <input
                type="text"
                value={formData.vaccinationData.batchNumber}
                onChange={(e) =>
                  handleInputChange(
                    "vaccinationData",
                    "batchNumber",
                    e.target.value
                  )
                }
                className={`w-full border rounded-lg px-3 py-2 ${
                  themeClasses.bg.input
                } ${
                  themeClasses.text.primary
                } focus:outline-none focus:border-cyan-500 ${
                  errors["vaccinationData.batchNumber"]
                    ? "border-red-500"
                    : "themeClasses.border.primary"
                }`}
                placeholder="e.g., RBX-1029"
              />
              {errors["vaccinationData.batchNumber"] && (
                <p className="text-red-400 text-sm mt-1">
                  {errors["vaccinationData.batchNumber"]}
                </p>
              )}
            </div>

            <div>
              <label
                className={`block text-sm mb-2 ${themeClasses.text.secondary}`}
              >
                Date Administered *
              </label>
              <input
                type="datetime-local"
                value={formData.vaccinationData.dateAdministered}
                onChange={(e) =>
                  handleInputChange(
                    "vaccinationData",
                    "dateAdministered",
                    e.target.value
                  )
                }
                className={`w-full border rounded-lg px-3 py-2 ${
                  themeClasses.bg.input
                } ${
                  themeClasses.text.primary
                } focus:outline-none focus:border-cyan-500 ${
                  errors["vaccinationData.dateAdministered"]
                    ? "border-red-500"
                    : "themeClasses.border.primary"
                }`}
              />
              {errors["vaccinationData.dateAdministered"] && (
                <p className="text-red-400 text-sm mt-1">
                  {errors["vaccinationData.dateAdministered"]}
                </p>
              )}
            </div>

            <div>
              <label
                className={`block text-sm mb-2 ${themeClasses.text.secondary}`}
              >
                Valid Until *
              </label>
              <input
                type="datetime-local"
                value={formData.vaccinationData.validUntil}
                onChange={(e) =>
                  handleInputChange(
                    "vaccinationData",
                    "validUntil",
                    e.target.value
                  )
                }
                className={`w-full border rounded-lg px-3 py-2 ${
                  themeClasses.bg.input
                } ${
                  themeClasses.text.primary
                } focus:outline-none focus:border-cyan-500 ${
                  errors["vaccinationData.validUntil"]
                    ? "border-red-500"
                    : "themeClasses.border.primary"
                }`}
              />
              {errors["vaccinationData.validUntil"] && (
                <p className="text-red-400 text-sm mt-1">
                  {errors["vaccinationData.validUntil"]}
                </p>
              )}
            </div>
          </div>

          <div className="mt-4">
            <label
              className={`block text-sm mb-2 ${themeClasses.text.secondary}`}
            >
              Notes
            </label>
            <textarea
              value={formData.vaccinationData.notes}
              onChange={(e) =>
                handleInputChange("vaccinationData", "notes", e.target.value)
              }
              className={`w-full border rounded-lg px-3 py-2 focus:outline-none focus:border-cyan-500 ${themeClasses.bg.input} ${themeClasses.text.primary} ${themeClasses.border.primary}`}
              rows="3"
              placeholder="Additional notes about the vaccination..."
            />
          </div>
        </div>

        {/* Veterinarian Information Section */}
        <div className="rounded-lg border ${themeClasses.bg.card} ${themeClasses.border.primary} p-6">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
            <User className="w-5 h-5 mr-2 text-purple-400" />
            Veterinarian Information
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label
                className={`block text-sm mb-2 ${themeClasses.text.secondary}`}
              >
                Veterinarian Name *
              </label>
              <input
                type="text"
                value={formData.vaccinationData.vetName}
                onChange={(e) =>
                  handleInputChange(
                    "vaccinationData",
                    "vetName",
                    e.target.value
                  )
                }
                className={`w-full border rounded-lg px-3 py-2 ${
                  themeClasses.bg.input
                } ${
                  themeClasses.text.primary
                } focus:outline-none focus:border-cyan-500 ${
                  errors["vaccinationData.vetName"]
                    ? "border-red-500"
                    : "themeClasses.border.primary"
                }`}
                placeholder="e.g., Dr. Sarah Johnson"
              />
              {errors["vaccinationData.vetName"] && (
                <p className="text-red-400 text-sm mt-1">
                  {errors["vaccinationData.vetName"]}
                </p>
              )}
            </div>

            <div>
              <label
                className={`block text-sm mb-2 ${themeClasses.text.secondary}`}
              >
                Contact Information *
              </label>
              <input
                type="text"
                value={formData.vaccinationData.vetContact}
                onChange={(e) =>
                  handleInputChange(
                    "vaccinationData",
                    "vetContact",
                    e.target.value
                  )
                }
                className={`w-full border rounded-lg px-3 py-2 ${
                  themeClasses.bg.input
                } ${
                  themeClasses.text.primary
                } focus:outline-none focus:border-cyan-500 ${
                  errors["vaccinationData.vetContact"]
                    ? "border-red-500"
                    : "themeClasses.border.primary"
                }`}
                placeholder="e.g., +47 123 456 78"
              />
              {errors["vaccinationData.vetContact"] && (
                <p className="text-red-400 text-sm mt-1">
                  {errors["vaccinationData.vetContact"]}
                </p>
              )}
            </div>

            <div>
              <label
                className={`block text-sm mb-2 ${themeClasses.text.secondary}`}
              >
                License Number *
              </label>
              <input
                type="text"
                value={formData.vaccinationData.vetLicense}
                onChange={(e) =>
                  handleInputChange(
                    "vaccinationData",
                    "vetLicense",
                    e.target.value
                  )
                }
                className={`w-full border rounded-lg px-3 py-2 ${
                  themeClasses.bg.input
                } ${
                  themeClasses.text.primary
                } focus:outline-none focus:border-cyan-500 ${
                  errors["vaccinationData.vetLicense"]
                    ? "border-red-500"
                    : "themeClasses.border.primary"
                }`}
                placeholder="e.g., VET-12345"
              />
              {errors["vaccinationData.vetLicense"] && (
                <p className="text-red-400 text-sm mt-1">
                  {errors["vaccinationData.vetLicense"]}
                </p>
              )}
            </div>

            <div>
              <label
                className={`block text-sm mb-2 ${themeClasses.text.secondary}`}
              >
                Clinic Address *
              </label>
              <input
                type="text"
                value={formData.vaccinationData.clinicAddress}
                onChange={(e) =>
                  handleInputChange(
                    "vaccinationData",
                    "clinicAddress",
                    e.target.value
                  )
                }
                className={`w-full border rounded-lg px-3 py-2 ${
                  themeClasses.bg.input
                } ${
                  themeClasses.text.primary
                } focus:outline-none focus:border-cyan-500 ${
                  errors["vaccinationData.clinicAddress"]
                    ? "border-red-500"
                    : "themeClasses.border.primary"
                }`}
                placeholder="e.g., 123 Main St, Oslo, Norway"
              />
              {errors["vaccinationData.clinicAddress"] && (
                <p className="text-red-400 text-sm mt-1">
                  {errors["vaccinationData.clinicAddress"]}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Submit Button */}
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={loading}
            className={`bg-cyan-600 hover:bg-cyan-700 disabled:${themeClasses.bg.tertiary} text-white px-6 py-3 rounded-lg font-semibold transition-colors flex items-center`}
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Submitting...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Submit Vaccination Record
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default VeterinarianDashboardPage;
